import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * StickyCTA — a persistent conversion bar that slides up after the user
 * scrolls past the hero. Full-width gold CTA on mobile, compact pill on
 * desktop bottom-right. Hidden while the final apply section is in view
 * to avoid redundancy.
 */
export default function StickyCTA() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const past = window.scrollY > 700;
      const apply = document.getElementById("apply");
      let nearApply = false;
      if (apply) {
        const r = apply.getBoundingClientRect();
        nearApply = r.top < window.innerHeight && r.bottom > 0;
      }
      setShow(past && !nearApply);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Mobile: full-width bottom bar */}
          <motion.div
            key="sticky-mobile"
            data-testid="sticky-cta-mobile"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-3"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(243,241,235,0.9) 40%, #f3f1eb 100%)",
            }}
          >
            <a
              href="https://forms.tgc.chat/book-now"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sticky-cta-mobile-btn"
              className="flex items-center justify-center gap-2.5 w-full py-4 rounded-full font-semibold text-[16px]"
              style={{
                background:
                  "linear-gradient(180deg, #f8e29a 0%, #e2be5f 45%, #c69a3c 100%)",
                color: "#0a0a0a",
                boxShadow: "0 20px 45px -12px rgba(212,175,55,0.65)",
              }}
            >
              Book Your Expert Strategy Call
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full"
                style={{ background: "#0a0a0a", color: "#f6dd8b" }}
              >
                →
              </span>
            </a>
            <p className="text-center text-[#0a0a0a]/45 text-[11px] mt-2">
              Only 3 spots left this month · No sales pitch
            </p>
          </motion.div>

          {/* Desktop: compact floating pill bottom-right */}
          <motion.a
            key="sticky-desktop"
            href="https://forms.tgc.chat/book-now"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-cta-desktop"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="hidden md:inline-flex fixed bottom-6 right-6 z-40 group items-center gap-3 pl-6 pr-2.5 py-2.5 rounded-full font-semibold text-[15px]"
            style={{
              background:
                "linear-gradient(180deg, #f8e29a 0%, #e2be5f 45%, #c69a3c 100%)",
              color: "#0a0a0a",
              boxShadow: "0 24px 50px -14px rgba(212,175,55,0.7)",
            }}
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[#0a0a0a] animate-ping opacity-40" />
              <span className="relative inline-flex rounded-full w-2 h-2 bg-[#0a0a0a]" />
            </span>
            Book Your Expert Strategy Call
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-transform duration-300 group-hover:translate-x-1"
              style={{ background: "#0a0a0a", color: "#f6dd8b" }}
            >
              →
            </span>
          </motion.a>
        </>
      )}
    </AnimatePresence>
  );
}
